import ChatIcon from "@mui/icons-material/Chat";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import { useFeatureGate } from "@statsig/react-bindings";
import React from "react";
import { toast } from "react-toastify";

import { IconButton } from "@/Components/v2/atoms/Button";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Tooltip } from "@/Components/v2/atoms/ToolTip";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useAnalytics } from "@/hooks/useAnalytics";
import { SHOW_ACTIVITY_LOG } from "@/pages/scenario-builder/statusConfig";
import { StatusEnum } from "@/pages/scenario-builder/v3/pages/scenario-form/types";

import { useDuplicateScenario } from "../api/duplicateScenario";
import { useScenariosV3Routes } from "../hooks/useScenariosV3Routes";
import { ScenarioType, ScenarioTypeEnum } from "../pages/scenario-form/types";
import Pill from "./ScenarioStatus/ScenarioStatusPill";

type ScenarioActionButtonsProps = {
  scenarioName: string;
  scenarioId: string;
  isActivityLogOpen: boolean;
  setIsActivityLogOpen: ((open: boolean) => void) | undefined;
  scenarioStatus: StatusEnum;
  handleUpdateScenario: (updatedScenarioStatus: StatusEnum) => void;
};

const ScenarioActionButtons: React.FC<ScenarioActionButtonsProps> = ({
  scenarioId,
  isActivityLogOpen,
  scenarioStatus,
  setIsActivityLogOpen,
}) => {
  const { navigateToScenarioForm } = useScenariosV3Routes();
  const { scenarioCreated } = useAnalytics();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  const isShowActivityLog = SHOW_ACTIVITY_LOG.includes(scenarioStatus);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.info("Scenario URL copied to clipboard");
  };

  const duplicateScenario = useDuplicateScenario({
    isScenariosApiEnabled,
    config: {
      onSuccess: (data) => {
        toast.success(`"${data.name}" duplicated`);
        scenarioCreated({
          scenarioType: ScenarioType.toString(
            data.scenarioType as ScenarioTypeEnum,
            data.scenarioCount || undefined
          ),
          scenarioId: data.uuid,
          scenarioName: data.name,
          isCloned: true,
        });
        navigateToScenarioForm(data.uuid);
      },
      onError: (data) => {
        toast.error(`"${data.name}" failed to duplicate. Please try again.`);
      },
    },
  });

  const handleDuplicateScenario = () => {
    duplicateScenario.mutate(scenarioId);
  };

  return (
    <>
      <Stack gap={2} direction="row" alignItems="center">
        <Tooltip title="Duplicate" placement="top">
          <IconButton onClick={handleDuplicateScenario}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Link" placement="top">
          <IconButton onClick={handleCopyLink}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
        {isShowActivityLog && setIsActivityLogOpen && (
          <Tooltip title="Activity Log" placement="top">
            <IconButton onClick={() => setIsActivityLogOpen(!isActivityLogOpen)}>
              <ChatIcon />
            </IconButton>
          </Tooltip>
        )}
        {scenarioStatus !== StatusEnum.Unknown && <Pill status={scenarioStatus} />}
      </Stack>
    </>
  );
};

export default ScenarioActionButtons;
